import { FC, useCallback, useEffect } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { WithChildren } from "_metronic/helpers";
import { getCompanyFromHost, selectCompanyId, selectFailed, selectLoading } from "./CompanyProviderSlice";
import { useSelector } from "react-redux";
import { Error404 } from "app/modules/errors/components/Error404";
import {
  getCompanyBranding,
  selectBrandingFailed,
  selectBrandingLoading,
  selectCompanyBranding,
} from "./CompanyBrandingSlice";
import { CompanyBrandingModel } from "services/api/cplauthapi/CPLAuthAPI";

const CompanyProvider: FC<WithChildren> = ({ children }) => {
  const thunkDispatch = useThunkDispatch();

  const getCompanyFailed = useSelector(selectFailed);
  const getCompanyLoading = useSelector(selectLoading);
  const companyID = useSelector(selectCompanyId);

  const companyBranding = useSelector(selectCompanyBranding);
  const getBrandingFailed = useSelector(selectBrandingFailed);
  const getBrandingLoading = useSelector(selectBrandingLoading);

  useEffect(() => {
    if (companyID === undefined && !getCompanyLoading && !getCompanyFailed) {
      thunkDispatch(getCompanyFromHost(window.location.host));
    }
  }, [companyID, getCompanyLoading, thunkDispatch, getCompanyFailed]);

  useEffect(() => {
    if (companyID !== undefined && companyBranding === undefined && !getBrandingFailed && !getBrandingLoading) {
      thunkDispatch(getCompanyBranding(companyID));
    }
  }, [companyID, thunkDispatch, getBrandingFailed, getBrandingLoading, companyBranding]);

  const loadBranding = useCallback(
    async (companyBranding: CompanyBrandingModel) => {
      // Load stylesheet
      if (companyBranding == null) return <>{children}</>;
      if (companyBranding.companyBrandingFileUrl != undefined) {
        const head = document.head;
        const link = document.createElement("link");
        link.type = "text/css";
        link.rel = "stylesheet";

        // Random number appended to prevent caching of css
        const randomNum = Math.floor(100000 + Math.random() * 900000);
        link.href = `${companyBranding.companyBrandingFileUrl}?${randomNum}`;

        head.appendChild(link);
      }

      if (companyBranding.clientPortalName != undefined) {
        document.title = companyBranding.clientPortalName;
      }

      if (companyBranding.bodyFontUrl != undefined) {
        // Load font
        const newFont = new FontFace("BrandFont", `url(${companyBranding.bodyFontUrl})`);
        await newFont.load();
        document.fonts.add(newFont);
      }

      if (companyBranding.headerFontUrl != undefined) {
        // Load font
        const newFont = new FontFace("SecondaryBrandFont", `url(${companyBranding.headerFontUrl})`);
        await newFont.load();
        document.fonts.add(newFont);
      }

      if (companyBranding.faviconUrl != undefined) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | undefined;
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.getElementsByTagName("head")[0].appendChild(link);
        }
        link.href = companyBranding.faviconUrl;
      }
    },
    [children]
  );

  useEffect(() => {
    if (companyBranding != undefined) {
      loadBranding(companyBranding);
    }
  }, [companyBranding, loadBranding]);

  return <>{getCompanyFailed ? <Error404 /> : children}</>;
};

export default CompanyProvider;
